import { useCallback, useEffect, useMemo } from "react";
import { IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";

import { hasElements } from "Utils/Array";
import { isFalse, isTrue } from "Utils/Boolean";
import { isFunction } from "Utils/Function";
import { useResults } from "./UseResults";
import { generateOnRenderLabel } from "../Choice";
import { IContentProps } from "../IContentProps";
import { IUseContent } from "./IUseContent";

export function useContent(props: IContentProps): IUseContent {
  const {
    results, cache, onSelect, onSelectResult, onIsRelevant, onAction
  } = props;

  const resultList = useResults(results);

  const onChangeChoice = useCallback(
    (_, option: IChoiceGroupOption) => {
      const { key } = option;
      const result = resultList.find((r) => r?.[`@resultId`] === key);
      onSelectResult(result);
      onSelect?.(result);
    },
    [onSelect, onSelectResult, resultList]
  );

  const options: IChoiceGroupOption[] = useMemo(
    () => resultList.map<IChoiceGroupOption>((result) => {
      const hasOnIsRelevant = isTrue(result?.[`@isOrigin`]) && isFunction(onIsRelevant);
      const onIsRelevantInt = hasOnIsRelevant ? onIsRelevant : null;

      const hasOnAction = isFalse(result?.[`@isOrigin`]) && isFunction(onAction);
      const onActionInt = hasOnAction
        ? () => {
          onSelectResult(result);
          onSelect?.(result);
          onAction?.(result);
        }
        : null;

      return {
        key: result[`@resultId`],
        text: `INTERNAL_NOT_USED`,
        onRenderLabel: generateOnRenderLabel(result, cache, onActionInt, onIsRelevantInt)
      };
    }),
    [cache, onAction, onIsRelevant, onSelect, onSelectResult, resultList]
  );

  useEffect(() => {
    for (const result of resultList) {
      if (isTrue(result?.[`@isResultSelected`])) {
        onSelectResult(result);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultList]);

  return {
    onChangeChoice,
    options,
    hasResults: hasElements(results)
  };
}
